// AdminChrome — the admin surface's chrome components (Users & Roles rows,
// admin sidebar, activity-log day/group/row + mini calendar, activity-scoring
// row), extracted verbatim from the pages/admin/{admin,logs,activity-scoring}.js
// PAGE modules (bundle-split T1b).
//
// WHY THIS FILE EXISTS: eager component registries (components/index.js here +
// shared/components/IdentityAdminComponents.js) must never import PAGE modules —
// a static `export { X } from '../pages/admin/admin.js'` pins the whole page
// body (route def, fetch, onCreate loaders) into the eager shell/boot chunk
// even though the routes themselves lazy-load via the pages/index.js lazyPage
// folds. Page trees resolve these components by bare key / string `extends`
// through the registry, so moving the defs here is transparent to the pages;
// the page modules keep compat re-exports so their public API is unchanged.
// Helpers shared by BOTH a moved component AND remaining page code (_shortDate,
// _shellDayKey, _isRowDirty) are exported from here and imported back by the pages —
// single source, never duplicated.

// Leaf module, NOT the functions barrel: this component is reachable from
// the workspace-shared components barrel (IdentityAdminComponents), which
// loads during shared's own init — importing the shell functions barrel
// from here re-entered the still-initializing shared barrel and crashed
// the shell boot (2026-08-12).
import { roleOf, roleColor, roleLabel, roleLabelKey } from '../functions/roles.js'

// ── /admin (Users & Roles) chrome ──────────────────────────────────────

export const AdminUserGroupHeader = {
  // The shared table kit's header row (design brief 4.5 item 2): a tonal
  // plate, eyebrow labels (`Y`, 500, uppercase, the quiet ink), no stroke.
  // It replaced a 2px `line` underline and 700 `caption` labels. The
  // columns and the responsive steps stay this table's own.
  extends: 'AppTableHead',
  columns: '2fr 1fr auto',
  // The scroll floor of the table, a spacing token (it was a raw 520px — the
  // /admin/teams member table uses the same `H`).
  minWidth: 'H',
  H01: { text: '{{ admin.users.col.name | polyglot }}' },
  H02: { text: '{{ admin.users.col.role | polyglot }}' },
  H03: { text: '', width: 'C2' },
  // iPad (≤1024): drop the floor so the header shrinks to fit the panel;
  // ≤768 it hides entirely (below). @tabletS precedes @mobileL so ≤768 wins.
  '@tabletS': { minWidth: '0' },
  '@mobileL': { display: 'none' }
}

export const AdminUserRow = {
  // The shared table kit's record row (design brief 4.5 item 3): `controlH`
  // tall, the `hover` fill, no stroke — rows separate by the table's gap.
  // It replaced a hairline `line` divider under every row and a `dialog`
  // theme hover.
  extends: 'AppTableRow',
  columns: '2fr 1fr auto',
  minWidth: 'H',
  cursor: 'pointer',
  // iPad (≤1024): drop the floor so the 3-col row shrinks to fit; ≤768 it
  // restacks (below). @tabletS precedes @mobileL so the stacked layout wins ≤768.
  '@tabletS': { minWidth: '0' },
  '@mobileL': {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"person person" "role action"',
    minWidth: '0',
    gap: 'Z1',
    padding: 'A',
    round: 'radiusControl',
    // A phone card per person: the `dialog` fill separates the cards, with
    // no stroke (§2.5).
    background: 'dialog'
  },

  // §unified-perms PR #8 — row click opens PersonDetailModal (effective
  // perms drill-down). EditBtn keeps its profile-navigation behavior so
  // operators can still jump to the canonical profile page.
  // Spread the explicit row fields the modal needs — NOT the smbls
  // state proxy `s`. Spreading the proxy enumerates whatever accessors
  // smbls happens to expose (root / parent / signal handles), and any
  // future minor-version bump that surfaces a new enumerable accessor
  // would silently capture a back-ref into root, breaking the next
  // state-clone path. Mirror the canvas peer at
  // team-and-agents.js _onNodeClick which spreads the underlying
  // person object for the same reason.
  onClick: (e, el, s) => {
    if (!s.email) return
    el.call('openPersonDetailModal', {
      email: s.email,
      name: s.name,
      avatar: s.avatar,
      userId: s.userId
    })
  },

  Left: {
    flow: 'x',
    align: 'center',
    gap: 'Z1',
    minWidth: '0',
    '@mobileL': { gridArea: 'person' },

    // House identity plate — `Monogram` key, NOT `Avatar`: the bare `Avatar`
    // key auto-extends the built-in (tag img), and initials text inside an
    // <img> is invalid. White-on-blue also measured 3.62:1 (needs 4.5).
    //
    // Two honest fields since the 2026-09-01 split (loadPrivateData.js
    // `_person`): `s.avatar` is a photo URL or EMPTY, `s.initials` carries
    // the letters. The old single overloaded field caused BOTH failure
    // modes in turn: painted a raw photo URL as garbled circle text
    // (AVATAR-URL-LEAK-1, tickets/sonnet.md) and, guarded the other way,
    // 404-fetched the letters as a path on every load — `hide:` is
    // display:none only, and a hidden <img> still requests its src.
    Monogram: {
      align: 'center center',
      width: 'B1',
      height: 'B1',
      round: '50%',
      color: 'title',
      fontSize: 'Z',
      fontWeight: '700',
      overflow: 'hidden',
      background: 'overlayStrong',
      flex: '0 0 auto',
      position: 'relative',

      MonogramImg: {
        tag: 'img',
        // `if:`, never `hide:` — a display:none <img> still FETCHES its
        // src, so a non-URL value 404ed against the origin on every load.
        // No node, no request.
        if: (el, s) => typeof s?.avatar === 'string' && /^(https?:|\/)/.test(s.avatar),
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        // Belt on top of the if: gate — a non-URL value never reaches src
        // (an absent src fires no request; src:'' can fetch the page URL).
        src: (el, s) =>
          typeof s?.avatar === 'string' && /^(https?:|\/)/.test(s.avatar) ? s.avatar : null,
        alt: (el, s) => s?.name || '',
        style: { objectFit: 'cover' }
      },
      MonogramText: {
        tag: 'span',
        hide: (el, s) => typeof s?.avatar === 'string' && /^(https?:|\/)/.test(s.avatar),
        text: (el, s) => s?.initials || ''
      }
    },
    Info: {
      flow: 'y',
      gap: 'V',
      flex: '1',
      minWidth: '0',
      Name: {
        tag: 'span',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.name
      },
      Email: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.email
      }
    }
  },

  // Role hue as a DOT + neutral ink (RmDot pattern) — white ink on the
  // user-configured role hue cannot be proven AA for arbitrary hues.
  RoleBadge: {
    tag: 'span',
    flow: 'x',
    align: 'center',
    gap: 'Y',
    padding: 'V Z1',
    round: 'radiusPill',
    alignSelf: 'center',
    justifySelf: 'flex-start',
    width: 'fit-content',
    background: 'overlayStrong',
    color: 'title',
    fontSize: 'Z2',
    fontWeight: '700',
    '@mobileL': {
      gridArea: 'role',
      justifySelf: 'start'
    },
    // `s.root.roles` stays undefined until `_loadRoles` resolves at least
    // once (functions/loadPrivateData.js) — before that, `roleOf` always
    // misses and the old unconditional `|| 'member'` fallback painted a
    // confident, WRONG "Member" for every row (including the signed-in
    // admin's own Owner row) that then flipped to the real role once the
    // fetch landed. A reader watching that transition on their OWN row sees
    // it as "my access just changed" (tickets/sonnet.md ROLE-FLIP-1). Render
    // a neutral loading state instead of a specific-but-unproven role until
    // `roles.byUser` genuinely exists — after that, an unmatched userId is a
    // real "member" default, not a loading gap.
    RoleDot: {
      tag: 'span',
      width: 'Y',
      height: 'Y',
      round: '50%',
      flex: '0 0 auto',
      background: (el, s) => {
        if (!s.root?.roles?.byUser) return 'caption'
        return roleColor(s, s.userId ? roleOf(s, s.userId) || 'member' : 'member')
      }
    },
    RoleText: {
      tag: 'span',
      text: (el, s) => {
        s.root.roles && s.root.roles.version
        if (!s.root?.roles?.byUser) return '…'
        const cur = s.userId ? roleOf(s, s.userId) || 'member' : 'member'
        // A system role resolves through the bundle (roles.js roleLabelKey).
        const key = roleLabelKey(cur)
        return key ? el.call('polyglot', key) : roleLabel(s, cur)
      }
    }
  },

  // The row action sits INSIDE a cell: the table kit gives every DIRECT
  // child of a row role="cell", and a <button> with role="cell" is no longer
  // a button to assistive tech (admin fix lane C, 2026-09-24). The button is
  // the admin compact control — it measured 26.7px (V Z1 padding), neither
  // control height, on all 79 rows of the live roster.
  Action: {
    alignSelf: 'center',
    '@mobileL': {
      gridArea: 'action',
      justifySelf: 'end'
    },
    EditBtn: {
      extends: 'AdminActionBtnCompact',
      text: '{{ common.action.edit | polyglot }}',
      // Seventy-nine buttons that all say "Edit" — the name says whose.
      ariaLabel: (el, s) =>
        el.call('polyglot', 'admin.users.editAria').replace('{name}', s.name || s.email || ''),
      onClick: (e, el, s) => {
        if (!s.email) return
        // Row click opens the perm-detail modal; the Edit button is the
        // explicit "navigate to profile" affordance. Stop propagation so
        // this click doesn't also fire the row's modal handler.
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
        const root = el.call('findRouteRoot', el)
        if (root) el.router(el.call('profilePath', s.email), root)
      }
    }
  }
}

// ── Admin sidebar (shared across all admin pages) ──
export const AdminSidebar = {
  extends: 'AppSidebar',
  tag: 'nav',
  ariaLabel: (el) => el.call('polyglot', 'admin.nav.ariaLabel'),
  // On the page-sidebar rule (shared functions/pageSidebar.js) for every
  // /admin page — one page key, `admin`: opposite a rail navbar, hideable.
  // The first group opens with its heading, so the button's corner is free.
  pageSidebar: true,
  PeopleSection: null,
  width: 'G',
  minWidth: 'G',
  '@mobileL': {
    width: '100%',
    minWidth: '0'
  },

  Top: null,
  // Admin is its own destination, not a canvas-view context — drop the shared
  // AppSidebar global view switcher (Frontend / Infra / Tasks / Agents) so the
  // admin rail shows only the admin sections (per Nika "remove Views section").
  ViewSwitcher: null,

  // THE GROUPS (persona audit WH-8): the rail's 19 entries ran as one list, a
  // business owner read "Client Module" and "Feature Flags" beside "Users &
  // Roles", and at 1366x768 the last entries sat cut off below the screen. The
  // entries now come in four named groups (functions/adminRail.js — the ONE
  // table, and the rule that folds the developer group on a business
  // workspace), and the list scrolls inside the rail (`flex` + `minHeight: 0`
  // + `overflowY`, the house rail shape — AppSidebar: "each rail's Middle has
  // its own overflow: auto").
  Middle: {
    flow: 'y',
    gap: 'Z',
    flex: '1',
    minHeight: '0',
    overflowY: 'auto',
    childExtends: 'AdminSidebarGroup',
    childrenAs: 'state',
    children: (el, s) => el.call('adminRailGroups', s.root)
  }
}

// One group of the admin rail. `state` is a functions/adminRail.js group:
// { key, label, foldable, folded, rows }. A group heading is a plain caption;
// the developer group is a native disclosure (`<details>` — keyboard and
// screen reader for free), folded on a business workspace.
export const AdminSidebarGroup = {
  tag: 'section',
  flow: 'y',
  gap: 'V',

  Heading: {
    if: (el, s) => !s.foldable,
    tag: 'h2',
    margin: '0',
    padding: 'Z Z1 0',
    fontSize: 'Z',
    fontWeight: '500',
    color: 'caption',
    // KEY-IN-STATE: the group carries a bundle key.
    text: (el, s) => el.call('polyglot', s.label || '')
  },
  AdminSidebarLinks: { if: (el, s) => !s.foldable },

  Fold: {
    if: (el, s) => !!s.foldable,
    tag: 'details',
    flow: 'y',
    gap: 'V',
    // Open unless the rule folds it (functions/adminRail.js); the person's own
    // toggle stands until that answer changes.
    open: (el, s) => (s.folded ? null : true),
    // A `summary` keeps its native list-item box, so the open / closed marker
    // is the browser's own (a flex box would drop it); the vertical padding
    // centres one line in the compact control height.
    // (the key names the base: FA806 — the house focus ring of `Focusable`)
    Focusable: {
      tag: 'summary',
      minHeight: 'controlHCompact',
      padding: 'Z Z1',
      boxSizing: 'border-box',
      round: 'radiusControl',
      fontSize: 'Z',
      fontWeight: '500',
      color: 'caption',
      cursor: 'pointer',
      ':hover': { background: 'hover', color: 'title' },
      ':active': { background: 'selected', color: 'title' },
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    AdminSidebarLinks: {}
  }
}

// The links of one group — the group's `rows`, one AdminSidebarLink each.
export const AdminSidebarLinks = {
  flow: 'y',
  gap: 'V',
  childExtends: 'AdminSidebarLink',
  childrenAs: 'state',
  children: (el, s) => s.rows || []
}

// ── Sidebar link ──
export const AdminSidebarLink = {
  extends: 'Link',
  attr: {
    // s.href, not el.href — el.href is the declared factory, so the
    // comparison could never match (same trap as the onClick fix below).
    'aria-current': (el, s) =>
      (s.root?.route || s.root?.currentPath || '/') === s.href ? 'page' : undefined
  },
  Icon: {
    name: (el, s) => s.icon || 'settings',
    boxSize: 'Z1',
    flexShrink: '0',
    ariaHidden: 'true'
  },
  // KEY-IN-STATE: the rail's rows carry bundle keys.
  Label: { text: (el, s) => el.call('polyglot', s.label || '') },
  // The compact control (`controlHCompact`, 36px): the `Z A` padding made
  // every rail row 38.75px — neither control height — on all 19 rows of all
  // 23 admin pages (the largest row of the admin audit, 2026-09-24). Compact,
  // not 44: nineteen rows at 44 would push the rail past a laptop screen.
  flow: 'x',
  align: 'center flex-start',
  minHeight: 'controlHCompact',
  padding: '0 Z1',
  gap: 'Z',
  round: 'radiusControl',
  fontSize: 'Z2',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  cursor: 'pointer',
  href: (el, s) => s.href,
  // Slot-aware navigation. Admin is a full-page destination now
  // (searchApps.isFullPageRoute), but if an admin page is EVER mounted
  // inside a PageSlot pane, a plain Link would fire a shell navigation
  // that leaves the slot open over a stale page (same-section, so
  // _dismissStaleSlot won't close it). When inside a pane, re-point that
  // pane's route instead; otherwise normal full-page routing.
  // (2026-07-25 per Nika "fix sidebar nav clicks when its in pageslot".)
  onClick: (e, el, s) => {
    // `el.href` is the FACTORY here ((el,s)=>s.href), not the resolved
    // string — v3.14 keeps declared factories on the element, so routing it
    // handed the router a function, matched nothing, and fell back to '/'
    // (the "every admin sidebar click lands on the dashboard" bug,
    // 2026-08-01 per Nika — broke the PageSlot branches identically via a
    // function in _canvasSlot). Read the state value the factory resolves
    // from, with the rendered attr as the fallback.
    const href = (s && s.href) || el.node?.getAttribute?.('href')
    if (!href || e.metaKey || e.ctrlKey) return // let cmd/ctrl-click open a real tab
    e.preventDefault()
    const inPrimary = el.node?.closest?.('[data-key="PanePrimary"]')
    const inSecond = el.node?.closest?.('[data-key="PaneSecond"]')
    if (inPrimary) return s.root.update({ _canvasSlot: href })
    if (inSecond) return s.root.update({ _canvasSlotSecond: href })
    const root = el.call('findRouteRoot', el) || el.getRoot()
    if (root) el.router(href, root)
  },
  // Active state via .isActive class (not reactive bg/color factories, which emit
  // inline styles that clobber :hover) so the hover wash actually renders.
  isActive: (el, s) => (s.root?.currentPath || '/') === s.href,
  color: 'subtitle',
  // Match the house sidebar rows (InfraSidebar / TicketsSidebar / FilesSidebar):
  // the shared `hover`/`selected` washes (§4.3) — a frosted wash on hover, a
  // slightly stronger wash + 700 weight when active. The old `overlay` token
  // rendered as a flat near-opaque fill that didn't read as a hover/active pill.
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  '.isActive': { background: 'selected', color: 'title', fontWeight: '700' }
}

// ── /admin/logs chrome — formatting helpers ────────────────────────────

const _shellTypeLabel = {
  standup: 'admin.activity.type.standup',
  ticket_update: 'admin.activity.type.ticketUpdate',
  note_edit: 'admin.activity.type.noteEdit',
  kb_edit: 'admin.activity.type.kbEdit',
  meet_call: 'admin.activity.type.meetCall'
}

// `el` (optional, last): with it the clock reads the way the SESSION language
// writes one — `hour12: true` forced `4:05 PM` onto a Georgian log, which
// reads the 24-hour clock. English keeps its 12-hour form through the locale.
const _shortTime = (ts, el) => {
  try {
    const d = new Date(ts)
    const out =
      el && typeof el.call === 'function'
        ? el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' })
        : ''
    return typeof out === 'string' && out
      ? out
      : d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch {
    return ''
  }
}

// Exported: the adminLogs PAGE also formats dates (Intro line, filter scope)
// — it imports this back from here rather than duplicating it.
//
// `el` (optional, last): with it the two words and the month name read in the
// SESSION language — `Today` / `Yesterday` / `19 Aug` were English on a
// Georgian admin log (live walk 2026-09-21). Without it the old output stays.
export const _shortDate = (ts, el) => {
  try {
    const d = new Date(ts)
    const today = new Date()
    // `el` may be a bare object (a specimen, a test): only a real element
    // carries `call`, and without one the old English output stays.
    const host = el && typeof el.call === 'function' ? el : null
    const word = (key, fallback) => {
      const text = host ? host.call('polyglot', key) : null
      return typeof text === 'string' && text && text !== key ? text : fallback
    }
    const sameDay = d.toDateString() === today.toDateString()
    if (sameDay) return word('common.time.today', 'Today')
    const yest = new Date()
    yest.setDate(today.getDate() - 1)
    if (d.toDateString() === yest.toDateString()) return word('common.time.yesterday', 'Yesterday')
    const sameYear = d.getFullYear() === today.getFullYear()
    const opts = sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
    const out = host ? host.call('localizedDate', d, opts) : ''
    return typeof out === 'string' && out ? out : d.toLocaleDateString(undefined, opts)
  } catch {
    return ''
  }
}

// The row's own readings — its words, its reference, where it links and what a
// click opens — are the History app's registered readers (functions/history.js:
// historyEventTitle, historyEventRef, historyEventHref, openHistoryEvent), read
// through `el.call`: /history and /admin/logs show the same audit-log rows. A
// private copy here kept the title chain that stops at `body` after History
// learned a standup's `summary_preview`, so every standup row on /admin/logs
// read its table name, "standup_activity".

// Exported: the adminLogs PAGE filters rows by day key (date-filter + counts)
// — it imports this back from here rather than duplicating it.
export const _shellDayKey = (r) => r.occurred_on || (r.created_at ? r.created_at.slice(0, 10) : '')

export const AdminLogDay = {
  flow: 'y',
  gap: 'Z1',

  DayHeader: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'Z',
    paddingBottom: 'V',
    // Borderless law — bold H3 Title + caption Stats already read as the
    // section head via typographic hierarchy + padding, same reasoning as
    // shared/AiActionConfirmCard's Header divider removal.

    // The day is the page's second heading level, under the page title (h1).
    // It was an h3 straight under the h1 — a skipped level (admin census,
    // 2026-09-24).
    Title: {
      tag: 'h2',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => _shortDate(s._day, el)
    },
    Stats: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) =>
        el
          .call(
            'polyglot',
            s._totalEvents === 1 ? 'admin.logs.day.eventCountOne' : 'admin.logs.day.eventCount'
          )
          .replace('{count}', s._totalEvents)
    }
  },

  Authors: {
    flow: 'y',
    gap: 'Z1',
    childExtends: 'AdminLogGroup',
    childrenAs: 'state',
    children: (el, s) => s._authors
  }
}

// One author's events on one day: an AdminCard — the branded `card` fill a
// step above the AppMain sheet, the card radius, no stroke. It was the
// `document-background` well (1.09:1 BELOW the sheet in dark) with a
// hairline `line` border as its only edge and a `dialog`-theme header band
// (admin census, 2026-09-24: 47 cards on /admin/logs). The well existed to
// pin a ground for the hue-inked type and ref labels; those labels are
// neutral now (AdminLogRow), so no ink on the card depends on its ground.
export const AdminLogGroup = {
  extends: 'AdminCard',
  gap: 'Y',
  padding: 'Z1',

  // Not `Header`: a key that names an HTML element renders as that element.
  AuthorBar: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z1',
    // The rows below inset their cells by `A`; the bar lines up with them.
    padding: 'Y A',
    '@mobileL': {
      flow: 'y',
      align: 'stretch'
    },

    LeftGroup: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',
      minWidth: '0',
      '@mobileL': { flexWrap: 'wrap' },

      // A background-image circle, not a bare `Avatar` key: that key extends
      // the framework built-in, an <img> that takes no `background` fill.
      // A member with no photo gets the generated avatar the /admin/usage
      // member rows use, not a raw `--color-blue` disc.
      Monogram: {
        boxSize: 'A',
        round: 'radiusPill',
        flexShrink: '0',
        background: (el, s) => {
          const me = el.call('findPerson', s._email)
          return `url(${me?.avatar || el.call('avatarUrl', me?.name || s._email || '?')}) center/cover`
        }
      },
      Name: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const me = el.call('findPerson', s._email)
          return me?.name || s._email
        }
      },
      Email: {
        hide: (el, s) => {
          const me = el.call('findPerson', s._email)
          return !me?.name
        },
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => s._email
      }
    },

    Stats: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) =>
        el
          .call(
            'polyglot',
            s._count === 1 ? 'admin.logs.author.statsOne' : 'admin.logs.author.stats'
          )
          .replace('{count}', s._count)
          .replace('{score}', s._totalScore)
    }
  },

  // The shared table kit (design brief 4.5): the events are an AppTable —
  // stroke-free rows that separate by its gap. The card's own inset keeps
  // the row hover capsule inside the card.
  Events: {
    extends: 'AppTable',
    childExtends: 'AdminLogRow',
    childrenAs: 'state',
    children: (el, s) => s._events
  }
}

// ── Calendar (mini month view) ─────────────────────────────────────────
const _monthGrid = (year, month) => {
  // Returns 6×7 = 42 cells for the month view (leading/trailing days included).
  const first = new Date(year, month, 1)
  const startDow = (first.getDay() + 6) % 7 // Monday-first
  const start = new Date(year, month, 1 - startDow)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    cells.push({
      _date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`,
      _day: d.getDate(),
      _inMonth: d.getMonth() === month
    })
  }
  return cells
}

const _stepMonth = (s, by) => {
  const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
  const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
  const next = new Date(y, m + by, 1)
  s.root?.update({
    _adminLogsCalYear: next.getFullYear(),
    _adminLogsCalMonth: next.getMonth()
  })
}

// The /admin/logs date filter (its card beside the list, pages/admin/logs.js).
// No plate of its own (the `overlay` wash it had is a veil, not a surface —
// it read 1.00:1) and no inset of its own, so seven day cells at the compact
// control height fit the card's `G` width. Every control is the compact
// 36px: the day cells were 23.45px and the month arrows 16.39px, 44 controls
// under the control law on /admin/logs (admin census, 2026-09-24).
export const AdminLogCalendar = {
  flow: 'y',
  gap: 'Y',

  onCreate: (el, s) => {
    const root = s.root
    if (!root) return
    const today = new Date()
    if (root._adminLogsCalYear == null || root._adminLogsCalMonth == null) {
      root.update({
        _adminLogsCalYear: today.getFullYear(),
        _adminLogsCalMonth: today.getMonth()
      })
    }
  },

  Nav: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    // The admin icon-only control: a named 36px square with the house hover
    // and press steps. The arrows were the text glyphs ‹ › in a 16.39px box.
    Prev: {
      extends: 'AdminIconBtn',
      ariaLabel: (el) => el.call('polyglot', 'common.action.previous'),
      Icon: { name: 'chevronLeft', boxSize: 'Z1' },
      onClick: (e, el, s) => _stepMonth(s, -1)
    },

    Label: {
      tag: 'span',
      flex: '1',
      textAlign: 'center',
      fontSize: 'Z2',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => {
        const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
        const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
        // The interface language, through polyglot's OWN resolver — never
        // `undefined` (which asks Intl for the BROWSER's locale, not the
        // language this member picked) and never a second hand-written
        // `root.lang > default > 'en'` chain.
        return el.call('localizedDate', new Date(y, m, 1), {
          month: 'long',
          year: 'numeric'
        })
      }
    },

    Next: {
      extends: 'AdminIconBtn',
      ariaLabel: (el) => el.call('polyglot', 'common.action.next'),
      Icon: { name: 'chevronRight', boxSize: 'Z1' },
      onClick: (e, el, s) => _stepMonth(s, 1)
    }
  },

  // The weekday heads read the bundle's short names (they were the literal
  // letters M T W T F S S — English on a Georgian log). Decorative for
  // assistive tech: every day cell names its full date.
  Dows: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: 'W',
    aria: { hidden: 'true' },
    // `Z`, never smaller: tokens are em, and `Y` read 9.7px in the rail.
    childProps: {
      tag: 'span',
      fontSize: 'Z',
      fontWeight: '500',
      color: 'caption',
      textAlign: 'center',
      whiteSpace: 'nowrap'
    },
    DowMon: { text: '{{ common.weekdayShort.mon | polyglot }}' },
    DowTue: { text: '{{ common.weekdayShort.tue | polyglot }}' },
    DowWed: { text: '{{ common.weekdayShort.wed | polyglot }}' },
    DowThu: { text: '{{ common.weekdayShort.thu | polyglot }}' },
    DowFri: { text: '{{ common.weekdayShort.fri | polyglot }}' },
    DowSat: { text: '{{ common.weekdayShort.sat | polyglot }}' },
    DowSun: { text: '{{ common.weekdayShort.sun | polyglot }}' }
  },

  // One choice among the days: a named group, each day a toggle button
  // (aria-pressed) — the chosen one is the brand's choice inversion.
  Grid: {
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'admin.logs.filterByDate'),
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: 'W',

    childExtends: 'AdminLogCalendarCell',
    childrenAs: 'state',
    children: (el, s) => {
      const y = s.root?._adminLogsCalYear ?? new Date().getFullYear()
      const m = s.root?._adminLogsCalMonth ?? new Date().getMonth()
      return _monthGrid(y, m)
    }
  }
}

// One day of the month: a real 36px button (`controlHCompact`), named by its
// full date. The states are CASE blocks, never reactive fill factories — a
// factory painted `blue.15` as an inline rgba() and made the framework force
// !important onto the hover rule (admin census, 2026-09-24):
//   rest        quiet ink, no fill — a day with no events;
//   hasLogs     the full `title` ink at 700 — a day with events (weight and
//               ink carry it, never a hue: hue does not mark data here);
//   :hover      the house `hover` fill and `title` ink;
//   isSelected  the brand's CHOICE inversion (`highlight` fill, its reversed
//               ink), aria-pressed — it was the `selected` wash;
//   isOutside   a leading/trailing day of the next or last month: not a
//               control at all (hidden, disabled, out of the tree) — it was a
//               focusable button that did nothing.
export const AdminLogCalendarCell = {
  tag: 'button',
  type: 'button',
  minHeight: 'controlHCompact',
  minWidth: '0',
  padding: '0',
  round: 'radiusControl',
  border: 'none',
  background: 'transparent',
  color: 'caption',
  // The surrounding size (`A` is 1em of the parent).
  fontSize: 'A',
  fontWeight: '400',
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'Z defaultBezier background',
  ':hover': { background: 'hover', color: 'title' },

  hasLogs: (el, s) => s._inMonth && (s.root?.adminLogs || []).some((r) => _shellDayKey(r) === s._date),
  isSelected: (el, s) => s._inMonth && s.root?._adminLogsSelectedDate === s._date,
  isOutside: (el, s) => !s._inMonth,
  '.hasLogs': { color: 'title', fontWeight: '700' },
  '.isSelected': { background: 'highlight', color: 'highlight-reversed', fontWeight: '700' },
  '.isOutside': { visibility: 'hidden' },

  disabled: (el, s) => !s._inMonth,
  aria: {
    pressed: (el, s) => (s._inMonth ? String(s.root?._adminLogsSelectedDate === s._date) : null),
    hidden: (el, s) => (s._inMonth ? null : 'true')
  },
  ariaLabel: (el, s) =>
    el.call('localizedDate', new Date(`${s._date}T00:00:00`), {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }),
  text: (el, s) => String(s._day),
  onClick: (e, el, s) => {
    if (!s._inMonth) return
    const cur = s.root?._adminLogsSelectedDate
    s.root?.update({ _adminLogsSelectedDate: cur === s._date ? null : s._date })
  }
}

export const AdminLogRow = {
  // The shared table kit's record row (design brief 4.5 item 3): `controlH`,
  // the `hover` fill, no stroke — it replaced a hairline `line` rule over
  // every row. The cells sit in NAMED areas, so a row whose `Ref` is hidden
  // keeps `Detail` in its own column instead of letting auto-placement pull
  // it into the ref slot. The first four tracks are CONTENT-sized (`auto`)
  // and each cell keeps the minimum width it had in the old flex row: fixed
  // tracks at those minimums wrapped "Ticket update" onto two lines and ran
  // the ref id into the detail text (measured live on dev, 2026-09-24). The
  // row font is the house sans: `fontFamily: 'mono'` named no token, so it
  // never painted a mono face; the score is the kit's numeric cell.
  extends: 'AppTableRow',
  columns: 'auto auto auto auto minmax(0, 1fr)',
  templateAreas: '"when type score ref detail"',
  alignItems: 'baseline',
  // The brand's compact size, `Z`. At `Y2` the tokens under it compounded
  // (em) below the smallest step: the time read 9.9px, the score 11.0px.
  fontSize: 'Z',
  lineHeight: '1.55',
  '@mobileL': {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"type score" "when when" "ref ref" "detail detail"',
    alignItems: 'start',
    gap: 'V Z',
    padding: 'Z1 A'
  },

  When: {
    gridArea: 'when',
    flow: 'y',
    gap: '0',
    minWidth: 'D',
    '@mobileL': {
      flow: 'x',
      gap: 'Z',
      minWidth: '0'
    },

    Date: {
      tag: 'span',
      color: 'subtitle',
      fontWeight: '700',
      text: (el, s) => _shortDate(s.occurred_on || s.created_at, el)
    },
    Time: {
      tag: 'span',
      color: 'caption',
      text: (el, s) => _shortTime(s.created_at, el)
    }
  },

  // The event type in the neutral ink. It was a per-type HUE (green, purple,
  // cerulean, accentInk) at 700: hue marks a status in the chrome register,
  // never a category, and purple read 2.5:1 on the card in dark.
  Type: {
    gridArea: 'type',
    tag: 'span',
    minWidth: 'C1',
    whiteSpace: 'nowrap',
    fontWeight: '500',
    color: 'title',
    '@mobileL': { minWidth: '0' },
    text: (el, s) => el.call('polyglot', _shellTypeLabel[s.activity_type] || s.activity_type || '')
  },

  Score: {
    extends: 'AppTableNum',
    gridArea: 'score',
    tag: 'span',
    minWidth: 'A2',
    '@mobileL': { minWidth: '0' },
    color: 'caption',
    fontWeight: '700',
    text: (el, s) => `+${s.score || 0}`
  },

  // The source reference. The cell WRAPS the control: the table kit gives
  // every DIRECT child of a row role="cell", and a <button role="cell"> is
  // no longer a button to assistive tech. The reference is a LINK to where
  // the event happened (a ticket, a note, a room) — an inline link, exempt
  // from the control heights like any link in running text; it was a 20.55px
  // <button> on all 200 rows (admin census, 2026-09-24). It keeps the
  // neutral ink and marks itself by its underline: the accent ink read
  // 3.92:1 on a hovered dark row. A reference with no destination is plain
  // text, never a dead control.
  Ref: {
    hide: (el, s) => !el.call('historyEventRef', s),
    gridArea: 'ref',
    minWidth: 'B1',
    whiteSpace: 'nowrap',
    '@mobileL': { minWidth: '0' },

    RefLink: {
      extends: 'Link',
      hide: (el, s) => !el.call('historyEventHref', s),
      href: (el, s) => el.call('historyEventHref', s),
      color: 'title',
      fontWeight: '700',
      textDecoration: 'underline',
      textDecorationColor: 'caption',
      transition: 'Z defaultBezier text-decoration-color',
      ':hover': { textDecorationColor: 'currentColor' },
      text: (el, s) => el.call('historyEventRef', s),
      onClick: (e, el, s) => {
        // A modified click is the browser's: a new tab or window.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
        e.preventDefault()
        e.stopPropagation()
        el.call('openHistoryEvent', s)
      }
    },
    RefText: {
      hide: (el, s) => !!el.call('historyEventHref', s),
      tag: 'span',
      color: 'caption',
      fontWeight: '700',
      text: (el, s) => el.call('historyEventRef', s)
    }
  },

  Detail: {
    gridArea: 'detail',
    tag: 'span',
    minWidth: '0',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    '@mobileL': {
      whiteSpace: 'normal',
      wordBreak: 'break-word'
    },
    text: (el, s) => el.call('historyEventTitle', s)
  }
}

// ── /admin/activity-scoring chrome — draft helpers ─────────────────────

// Exported: the adminActivityScoring PAGE's Save flow filters rows through the
// same dirty check — it imports this back from here rather than duplicating it.
export const _isRowDirty = (r) => r._draftEnabled !== r.enabled || r._draftScore !== r.score
const _patchRow = (rows, type, patch) =>
  rows.map((r) => (r.activity_type === type ? { ...r, ...patch } : r))
const _commitDraft = (root, rows) =>
  root?.update({
    activityScoring: rows,
    activityScoringDirty: rows.some(_isRowDirty),
    // A fresh edit supersedes whatever the last save attempt reported —
    // the stale "wasn't saved" banner must not survive the user acting on
    // it (tickets/sonnet.md P2).
    _activityScoringSaveError: null
  })

// ── Row ─────────────────────────────────────────────────────────────
export const AdminActivityScoringRow = {
  // The shared table kit's record row (design brief 4.5 item 3): `controlH`,
  // the `hover` fill, no stroke — it replaced a hairline `line` divider
  // under every row. The four flex ratios became the table's column track,
  // shared with the header (pages/admin/activity-scoring.js).
  extends: 'AppTableRow',
  columns: '2fr 3fr 1fr 1fr',
  // No opacity: `.5` on the whole row faded every label under the floor. An
  // activity that is switched off reads by its switch and by the quiet
  // caption ink on its name (Activity.Label below).
  '@mobileL': {
    gridTemplateColumns: 'minmax(0, 1fr)',
    rowGap: 'Z',
    padding: 'Z1 A'
  },

  Activity: {
    flow: 'y',
    gap: 'V',

    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '700',
      // Live-derived like the switch: the row's state is a snapshot.
      color: (el, s) => {
        const live = (s.root?.activityScoring || []).find(
          (r) => r.activity_type === s.activity_type
        )
        return (live ? live._draftEnabled : s._draftEnabled) ? 'title' : 'caption'
      },
      text: (el, s) => el.call('polyglot', s.label || s.activity_type || '')
    },
    // An id: the `Code` face (`mono` is no font token and painted the
    // default face).
    Slug: {
      tag: 'code',
      fontSize: 'Y1',
      color: 'caption',
      fontFamily: 'Code',
      text: (el, s) => s.activity_type || ''
    }
  },

  Description: {
    fontSize: 'Y2',
    color: 'caption',
    lineHeight: '1.4',
    text: (el, s) => s.description || ''
  },

  EnabledCell: {
    align: 'center center',

    // House SwitchInput (one switch language across admin — the old
    // hidden-checkbox + ':checked + div' green toggle was the last
    // divergent recipe, 2026-07-25 admin consistency pass).
    Toggle: {
      extends: 'SwitchInput',
      // Named by its row: a bare role=switch reads "switch, on" with no subject.
      ariaLabel: (el, s) => el.call('polyglot', s.label || s.activity_type || ''),
      // Live-derive from root.activityScoring — the childrenAs:'state' row
      // snapshot goes stale after _commitDraft (v3.14; the native checkbox
      // this replaced masked it by holding checked state in the DOM).
      onCheck: (el, s) => {
        const live = (s.root?.activityScoring || []).find(
          (r) => r.activity_type === s.activity_type
        )
        return !!(live ? live._draftEnabled : s._draftEnabled)
      },
      onClick: (e, el, s) => {
        const live = (s.root?.activityScoring || []).find(
          (r) => r.activity_type === s.activity_type
        )
        const cur = live ? live._draftEnabled : s._draftEnabled
        const rows = _patchRow(s.root?.activityScoring || [], s.activity_type, {
          _draftEnabled: !cur
        })
        _commitDraft(s.root, rows)
      }
    }
  },

  ScoreCell: {
    align: 'center right',

    // The shared field kit's control (AppInput: `controlH`, the control
    // radius, `Z2`, the `field` plate with its one hairline edge, the quiet
    // placeholder ink, the global focus ring). It was a hand-rolled 32.16px
    // input on the `overlay` wash with no name (admin census, 2026-09-24).
    // The key names the kit control itself: a key `Input` also extends the
    // framework's Input atom (whose default `type: 'input'` is invalid).
    AppInput: {
      type: 'number',
      min: '0',
      max: '100',
      step: '1',
      width: 'D',
      textAlign: 'right',
      ariaLabel: (el, s) =>
        `${el.call('polyglot', s.label || s.activity_type || '')} · ${el.call(
          'polyglot',
          'admin.activityScoring.col.score'
        )}`,
      // The reactive `value` alone: an onCreate that wrote `el.node.value`
      // was a direct DOM write (Rule 30, frank FA511) for what this prop does.
      value: (el, s) => String(s._draftScore ?? s.score ?? 0),
      onInput: (e, el, s) => {
        const n = parseInt(el.node.value, 10)
        const next = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0
        const rows = _patchRow(s.root?.activityScoring || [], s.activity_type, {
          _draftScore: next
        })
        _commitDraft(s.root, rows)
      }
    }
  }
}

// ── /admin/usage — usage-by-actor pure helpers (CREDITS-ATTR-3) ───────────
//
// Same bundle-split-T1b reason as every other export in this file EXPLICITLY
// SHARPENED: these are pure, no-DOM shaping functions with no `pages/`
// dependency of their own, but `pages/admin/usage.js` cannot be imported
// bare in Node/bun for direct unit testing — it (transitively, via
// shared/pages/identity-index.js's barrel) hits the SAME pre-existing
// circular-import TDZ crash ("Cannot access 'adminUsage' before
// initialization") that motivated this file's split in the first place.
// Defined here instead so __tests__/creditsAttribution.test.js can import
// them directly; `usage.js` imports them back (same shape as this file's
// `_shellDayKey`/`_shortDate`/`_isRowDirty` re-imports for logs.js/
// activity-scoring.js) and uses them exactly like its other local pure
// helpers (never via el.call — these aren't registered functions, matching
// this page's existing `_fmtCredits`/`_ledgerRows`/`_tierList` convention).
//
// tickets/fable.md CREDITS-ATTR-3, standing on the actor/liability contract
// CREDITS-ATTR-1 landed server-side (server `b99f86b0`,
// server/src/core/constants/creditActors.js). Three constraints, in order
// of how badly the page lies if violated:
//  1. A member ABSENT from the response is not "0" — `_attrMembers` only
//     ever returns rows the query actually reported; nothing here backfills
//     a workspace member roster with fabricated zeros.
//  2. `createdBy`/member attribution is never liability — every member row
//     is credits the WORKSPACE spent because that person caused it, never a
//     personal balance.
//  3. `systemCause` is groupable, not a display string — `_attrSystem`
//     resolves it through `_SYSTEM_CAUSE_LABELS` (never prints the raw enum
//     token).

// Bounded SYSTEM_CAUSES enum (server creditActors.js) → a human label. Real
// words, never the raw underscore token.
const _SYSTEM_CAUSE_LABELS = {
  deploy: 'Deploys',
  build: 'Builds',
  github_workflow: 'admin.usage.cause.githubWorkflow',
  cdn_egress: 'admin.usage.cause.cdnEgress',
  storage_snapshot: 'admin.usage.cause.storageSnapshot',
  webhook_delivery: 'admin.usage.cause.webhookDelivery',
  template_install: 'admin.usage.cause.templateInstall',
  scheduled_reindex: 'admin.usage.cause.scheduledReindex',
  retention_sweep: 'admin.usage.cause.retentionSweep',
  policy_eval: 'admin.usage.cause.policyEval',
  scim_sync: 'admin.usage.cause.scimSync',
  admin_adjustment: 'admin.usage.cause.adminAdjustment',
  other: 'admin.usage.cause.other'
}

// Self-contained fallback humanizer for a cause the table above doesn't
// know yet (the server can add a new SYSTEM_CAUSES member before this map
// is updated) — deliberately NOT sharing usage.js's `_humanizeKey` (that
// one's acronym table is tier-governance-specific and unrelated here).
const _humanizeSystemFallback = (key) => {
  if (!key) return ''
  return String(key)
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

export const _humanizeSystemCause = (cause) =>
  _SYSTEM_CAUSE_LABELS[cause] || _humanizeSystemFallback(cause) || 'admin.usage.cause.other'

// `lang` is the resolved interface language (`el.call('getActiveLang')`). It is
// a PARAMETER rather than a read inside the helper because this is a pure
// module-scope formatter with no element — and it defaults to `en` rather than
// to the browser locale, so an unpassed call is honest English, not a third
// language nobody chose.
// The second argument is the ELEMENT (preferred) or, for an old caller, a
// language code. Passing the language alone is not enough: Chrome ships no
// Georgian locale data, so `toLocaleDateString('ka', …)` silently resolves to
// en-GB and prints `13 Aug 2026`. With the element the date goes through the
// shared `localizedDate`, which takes the month name from the bundle there.
export const _fmtDateOnly = (d, elOrLang) => {
  if (!d) return ''
  try {
    const opts = { month: 'short', day: 'numeric', year: 'numeric' }
    const el = elOrLang && typeof elOrLang.call === 'function' ? elOrLang : null
    const out = el ? el.call('localizedDate', d, opts) : ''
    if (typeof out === 'string' && out) return out
    const lang = typeof elOrLang === 'string' && elOrLang ? elOrLang : 'en'
    return new Date(d).toLocaleDateString(lang, opts)
  } catch {
    return String(d)
  }
}

const _attrCreditsSum = (rows) => rows.reduce((n, r) => n + (Number(r.credits) || 0), 0)

// Members — ONLY rows the query actually reported. A member with zero
// attributed activity in the window never appears here at all; this
// function must never synthesize an entry for someone absent from the
// response (constraint #1 above — a fabricated 0 is the page lying about a
// colleague's spend). `share` is this member's slice of MEMBER-attributed
// spend (not of the workspace total) — comparing a person against
// System/Machine spend they don't control would dilute the number into
// something unreadable.
export const _attrMembers = (data) => {
  const rows = Array.isArray(data?.members) ? data.members : []
  const total = _attrCreditsSum(rows)
  return rows
    .slice()
    .sort((a, b) => (Number(b.credits) || 0) - (Number(a.credits) || 0))
    .map((r) => {
      const credits = Number(r.credits) || 0
      return {
        userId: r.userId,
        name: r.name || r.email || 'admin.usage.unknownMember',
        email: r.email || '',
        avatar: r.avatar || '',
        credits,
        events: r.eventCount ?? r.events ?? 0,
        share: total > 0 ? credits / total : 0
      }
    })
}

// System — broken down by cause (never a single "System: N" lump). Sorted
// descending so the biggest automated cost leads.
export const _attrSystem = (data) => {
  const rows = Array.isArray(data?.system) ? data.system : []
  return rows
    .slice()
    .sort((a, b) => (Number(b.credits) || 0) - (Number(a.credits) || 0))
    .map((r) => ({
      cause: r.cause || 'other',
      label: _humanizeSystemCause(r.cause),
      credits: Number(r.credits) || 0,
      events: r.eventCount ?? r.events ?? 0
    }))
}

// Machines — API tokens, installed integrations, agents. A real identity
// that is not a person, sorted descending.
export const _attrMachines = (data) => {
  const rows = Array.isArray(data?.machines) ? data.machines : []
  return rows
    .slice()
    .sort((a, b) => (Number(b.credits) || 0) - (Number(a.credits) || 0))
    .map((r) => ({
      label: r.machineLabel || r.label || 'admin.usage.unlabeledMachine',
      credits: Number(r.credits) || 0,
      events: r.eventCount ?? r.events ?? 0
    }))
}

// Pre-attribution history — its OWN bucket, permanent (rows written before
// CREDITS-ATTR-1 have no actorType at all and never will). Never rendered
// as part of Members or System; `before` is the cutover date this workspace
// stops being able to attribute, when the server publishes it.
export const _attrUnattributed = (data) => {
  const u = data?.unattributed || {}
  return {
    credits: Number(u.credits) || 0,
    events: u.eventCount ?? u.events ?? 0,
    before: u.before || null
  }
}

// Headline total — members + system + machines (never includes
// `unattributed`, which by definition isn't attributed to anything this
// section groups by). Prefers the server's own `totalCredits` when present
// so a workspace-side rounding/adjustment row the sub-arrays don't carry
// still reconciles; falls back to summing the three arrays client-side.
export const _attrTotal = (data) => {
  const explicit = Number(data?.totalCredits)
  if (Number.isFinite(explicit)) return explicit
  const members = Array.isArray(data?.members) ? data.members : []
  const system = Array.isArray(data?.system) ? data.system : []
  const machines = Array.isArray(data?.machines) ? data.machines : []
  return _attrCreditsSum(members) + _attrCreditsSum(system) + _attrCreditsSum(machines)
}

// ── subscription-tier perk breakdown (PRICE-3/PRICE-4, "clear breakdown
// whats included in each plan") — extracted here (bundle-split T1b's
// established convention, per this file's header note) SPECIFICALLY so it
// is a real, importable, behaviorally-testable pure function:
// pages/admin/usage.js cannot be imported bare in Node/bun for direct unit
// testing (see the block comment above `_humanizeSystemCause` for the
// circular-import TDZ this file already works around). `_tierPerks` is
// consumed by pages/admin/usage.js's Section_Tiers card grid.
// tickets/sonnet.md BILLING-TRUST-1 #5 (2026-08-14): `gb`/`csm`/`pdf` added
// — `_humanizeKey` is now ALSO reused by pages/admin/usage.js's rate-card
// rows (`_ratesToRows`), not just tier governance/support, so it needs to
// cover the rate-card's own vocabulary (`egress_gb`, `storage_gb_month`,
// `brandbook_pdf`, and `support: 'dedicated_csm'` → the "Dedicated Csm
// support" bug — `csm` fell through to plain capitalize-first-letter).
const _TIER_ACRONYMS = {
  sso: 'SSO',
  rbac: 'RBAC',
  sla: 'SLA',
  saml: 'SAML',
  scim: 'SCIM',
  gb: 'GB',
  csm: 'CSM',
  pdf: 'PDF'
}

export const _humanizeKey = (key) => {
  if (key == null) return ''
  return String(key)
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => {
      const lc = w.toLowerCase()
      return _TIER_ACRONYMS[lc] || lc.charAt(0).toUpperCase() + lc.slice(1)
    })
    .join(' ')
}

// Perk lines — derived ONLY from what the rate card actually publishes
// (`serializeTiers` in server CreditsController.js: governance, envs/
// customEnvsAllowed, rateLimitPerMin, includedStorageGb, workspaceCap,
// projectCap, capabilities, support), never invented copy. Previously
// capped at 5, which silently dropped real entitlements (storage, seat/
// workspace caps, the platform-capability count) off the card — a plan a
// prospect can't see the full shape of is not a real breakdown.
//
// No outer truncation here on purpose (design-direction.md §9 counted
// density caps still apply, just not via a slice that would silently drop
// a category): the function is already self-limiting by construction —
// governance is capped at 4 entries, and the remaining 7 categories
// (envs, storage, rate-limit, workspace cap, project cap, capability
// count, support) each contribute AT MOST one line — so the real-world
// ceiling is 11 short one-line perks for even the richest tier
// (Enterprise). That is the actual breakdown Nika asked for; an arbitrary
// second cap on top of it would re-introduce the exact "silently drops a
// real entitlement" bug this rewrite fixes.
// `el` resolves every line through polyglot, in the session language — the
// perks were English code strings on a Georgian page (admin fix lane D,
// 2026-09-24; the keys shipped in cfbab8270). Without an element the KEYS
// come back, never English literals.
export const _tierPerks = (t, el) => {
  if (!t || typeof t !== 'object') return []
  const say = (key) => (el && typeof el.call === 'function' ? el.call('polyglot', key) : key)
  const perks = []
  ;(Array.isArray(t.governance) ? t.governance : [])
    .slice(0, 4)
    .forEach((g) => perks.push(_humanizeKey(g)))
  if (Array.isArray(t.envs) && t.envs.length) {
    const n = t.envs.length
    const label =
      n === 1
        ? say('admin.usage.perk.environmentOne')
        : say('admin.usage.perk.environments').replace('{count}', n)
    perks.push(
      t.customEnvsAllowed ? say('admin.usage.perk.customEnvs').replace('{label}', label) : label
    )
  }
  if (t.includedStorageGb != null) {
    perks.push(
      say('admin.usage.perk.storageIncluded').replace(
        '{gb}',
        Number(t.includedStorageGb).toLocaleString('en-US')
      )
    )
  }
  if (t.rateLimitPerMin != null) {
    perks.push(
      say('admin.usage.perk.rateLimit').replace(
        '{count}',
        Number(t.rateLimitPerMin).toLocaleString('en-US')
      )
    )
  } else {
    perks.push(say('admin.usage.perk.customRateLimit'))
  }
  if (t.workspaceCap != null) {
    perks.push(
      t.workspaceCap === 1
        ? say('admin.usage.perk.workspaceOne')
        : say('admin.usage.perk.workspaces').replace('{count}', t.workspaceCap)
    )
  } else {
    perks.push(say('admin.usage.perk.unlimitedWorkspaces'))
  }
  if (t.projectCap != null) {
    perks.push(
      t.projectCap === 1
        ? say('admin.usage.perk.projectOne')
        : say('admin.usage.perk.projects').replace('{count}', t.projectCap)
    )
  } else {
    perks.push(say('admin.usage.perk.unlimitedProjects'))
  }
  if (Array.isArray(t.capabilities) && t.capabilities.length) {
    const n = t.capabilities.length
    perks.push(
      n === 1
        ? say('admin.usage.perk.capabilityOne')
        : say('admin.usage.perk.capabilities').replace('{count}', n)
    )
  }
  if (t.support) {
    perks.push(say('admin.usage.perk.support').replace('{name}', _humanizeKey(t.support)))
  }
  return perks
}

// ─── /admin/usage — tier key → Mongo Plan id (redesign "Upgrade" CTA) ─────
//
// tickets/fable.md PRICE-3 redesign, Nika 2026-08-13: "Request upgrade → >>
// Upgrade - goes to stipe." The rate card intentionally does NOT publish a
// Stripe lookup key per tier (server CreditsController.js `serializeTiers`:
// "checkout goes through planId — so it does not leave the server") — this
// is the resolvable half of that gap: `sdk.getPlansWithPricing()` → `GET
// /plans/pricing` (server `PlanService.listPublicPlansWithPricing`) returns
// real Mongo `Plan` documents joined to the SAME tier catalog via
// `plan.tierKey`, each carrying its own `_id` — the actual `planId` the
// checkout call wants. Extracted here (bundle-split T1b's established
// convention) so it is a real, importable, behaviorally-testable pure
// function, same reasoning as `_tierPerks` above.
//
// Defensive `.data` unwrap matches every other `_unwrap*` helper on this
// page (`_unwrapRates`/`_unwrapByActor`) — `sdk.getPlansWithPricing()`
// already resolves the bare array, but a raw `{success,data}` envelope
// (or a `{error}` optional-call fallback, which is neither an array nor
// carries `.data`) must resolve to `null`, never throw.
export const _planIdForTier = (tierKey, plansResponse) => {
  if (!tierKey) return null
  const list = Array.isArray(plansResponse)
    ? plansResponse
    : Array.isArray(plansResponse?.data)
      ? plansResponse.data
      : null
  if (!list) return null
  const match = list.find((p) => p && (p.tierKey === tierKey || p.tier === tierKey))
  return match?._id || match?.id || null
}

// SIGNUP-INTENT → direct purchase (symbols.app/built-with). The first sign-in
// after a paid-plan signup routes to /admin/usage?checkout=<tier>
// (shared/functions/signupIntent.js). The page captures the request ONCE and
// drops the query from the address bar at once, so a refresh, Back, or the
// Stripe success/cancel return (both reuse the current URL) can never start a
// second checkout. The one-shot request waits in sessionStorage — no root
// state key — until the tier's own UpgradeBtn renders and hands it to the
// same `_startUpgrade` a click runs. Only pro / scale are accepted.
export const _captureSignupCheckout = () => {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const want = url.searchParams.get('checkout')
    if (!want) return null
    url.searchParams.delete('checkout')
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    if (want !== 'pro' && want !== 'scale') return null
    window.sessionStorage.setItem('signup_checkout_want', want)
    return want
  } catch (_) {
    return null
  }
}

// true ONCE, for the tier the capture recorded; the request is consumed.
export const _takeSignupCheckoutFor = (tierKey) => {
  if (typeof window === 'undefined' || !tierKey) return false
  try {
    if (window.sessionStorage.getItem('signup_checkout_want') !== tierKey) return false
    window.sessionStorage.removeItem('signup_checkout_want')
    return true
  } catch (_) {
    return false
  }
}

// ─── /admin/usage row components (registration split) ────────────────────
//
// `AttrMemberRow`, `AttrMachineChip` and `AdminLedgerRow` are mounted by
// pages/admin/usage.js through bare-STRING `childExtends`, which resolve
// against `context.components` — the registry this file feeds via
// components/index.js. They lived in the PAGE module until 2026-08-13 and
// were therefore never registered anywhere: smbls's `resolveExtend`
// (smbls/packages/element/src/extends.js:56) returns `null` for an unknown
// name and its caller skips the merge without throwing or warning, so each
// row rendered as a bare untagged <div>. "Recent activity" on /admin/usage
// drew nothing at all — not even its own "No activity yet" empty state,
// which is built from the same unresolved component — while the ledger API
// had been returning real rows the entire time. Exactly the failure the
// AdminModule* block in components/index.js documents for the /admin/module
// Grant tab.
//
// They live HERE rather than being re-exported straight off the page module
// because components/index.js is imported EAGERLY by context.js while
// /admin/usage is lazy (`lazyPage(() => import('./admin/usage.js'))` in
// pages/index.js). A static re-export would pin all ~85KB of that page back
// into the boot chunk and reintroduce the circular-import TDZ crash its own
// header documents — the same trade bundle-split T1b already made for
// AdminLogRow / AdminActivityScoringRow above.

// Shared by the three components below and by pages/admin/usage.js, which
// imports it from here alongside the `_attr*` shaping helpers. One
// definition, no second copy to drift: a prop factory must never close over
// a const that lives in a module scope other than its own.
export const _fmtCredits = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
}

// An amount with its unit, in the session language ("{credits} cr" — კრ in
// Georgian). It was the English suffix ` cr` in eleven places.
export const _fmtCreditsUnit = (el, n) =>
  el.call('polyglot', 'admin.usage.unit.credits').replace('{credits}', _fmtCredits(n))

// Member spend row — a person did a thing. Identity (avatar + name) leads;
// credits + share-of-member-spend trail. NEUTRAL ink only, by design: a cap
// is a limit, not a target, and this page never ranks a human as a "worst
// offender" — no dangerInk/warningInk/red badge on a name, sort-by-spend
// is as far as ranking goes.
export const AttrMemberRow = {
  // The shared table kit's record row (design brief 4.5 item 3): `controlH`,
  // no stroke, identity on the left and the amount in its own column. The
  // hover fill stays off for the empty-state line below — it is not a
  // record, so it must not answer the pointer like one.
  extends: 'AppTableRow',
  columns: 'minmax(0, 1fr) auto',
  '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 'V' },
  ':hover': { background: (el, s) => (s._empty ? 'transparent' : 'hover') },

  // Empty-state sentinel — MembersCard.Rows returns this ONE row shape when
  // the query reported zero attributed members. A plain caption line, no
  // avatar, no value column, so it can never be mistaken for a real row
  // (constraint #1: never the same glyph as someone who genuinely spent 0).
  EmptyText: {
    hide: (el, s) => !s._empty,
    gridColumn: '1 / -1',
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => el.call('polyglot', s.label || 'admin.usage.noRecordedUsage')
  },

  Left: {
    hide: (el, s) => !!s._empty,
    flow: 'x',
    align: 'center',
    gap: 'Z',
    minWidth: '0',

    // Background-image circle, NOT a bare `Avatar:` key — the bare `Avatar`
    // key auto-extends the framework built-in (tag img), which cannot take
    // a `background: url(...)` fill (same trap AdminChrome.js's Monogram
    // comment documents for the /admin Users & Roles row).
    Monogram: {
      width: 'B1',
      height: 'B1',
      round: '50%',
      flexShrink: '0',
      background: (el, s) =>
        `url(${s.avatar || el.call('avatarUrl', s.name || s.email || '?')}) center/cover`
    },

    Info: {
      flow: 'y',
      gap: 'V',
      minWidth: '0',
      Name: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        // KEY-IN-STATE: a real member's NAME, or the bundle key the
        // attribution mapper substitutes when the server has neither.
        text: (el, s) => el.call('polyglot', s.name || '')
      },
      // `widgetPlate`: the brand's neutral plate for a track — it reads on a
      // card in both schemes (`overlayStrong` is a sheet wash).
      ShareTrack: {
        width: 'D',
        maxWidth: '100%',
        height: 'X',
        round: 'radiusPill',
        background: 'widgetPlate',
        overflow: 'hidden',
        ShareFill: {
          height: '100%',
          round: 'radiusPill',
          background: 'title.35',
          transition: 'A defaultBezier width',
          width: (el, s) => `${Math.max(Math.round((s.share || 0) * 100), s.credits > 0 ? 3 : 0)}%`
        }
      }
    }
  },

  Right: {
    hide: (el, s) => !!s._empty,
    flow: 'y',
    align: 'flex-end',
    gap: 'V',
    '@mobileL': { justifySelf: 'end' },
    // The amount is the kit's numeric cell: the Code face at Z, right-
    // aligned; figures are tabular by the brand's html default (D-7).
    Credits: {
      extends: 'AppTableNum',
      tag: 'span',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => _fmtCreditsUnit(el, s.credits)
    },
    Share: {
      tag: 'span',
      fontSize: 'Y1',
      color: 'caption',
      text: (el, s) => (s.share > 0 ? `${Math.round(s.share * 100)}%` : '')
    }
  }
}

// Machine spend chip — API tokens, integrations, agents. Deliberately a
// compact tag/pill, not a full-width row like AttrMemberRow: this is a
// configuration you can change, not a behaviour, and reading as a "tag
// cloud" rather than a "people list" is most of how Members/System/
// Machines stay visually distinguishable without reading the labels.
export const AttrMachineChip = {
  flow: 'x',
  align: 'center',
  gap: 'V',
  padding: 'V1 Z',
  round: 'radiusPill',
  // The neutral plate that reads on a card (`overlayStrong` is a sheet wash).
  background: 'widgetPlate',
  maxWidth: '100%',

  Icon: { name: 'terminal', boxSize: 'Y1', color: 'caption', flexShrink: '0' },
  Label: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'E',
    text: (el, s) => el.call('polyglot', s.label || '')
  },
  // The quiet ink alone — `.5` opacity faded it under the floor.
  Dot: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '·' },
  Credits: {
    tag: 'span',
    fontFamily: 'Code',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'title',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: '0',
    text: (el, s) => _fmtCreditsUnit(el, s.credits)
  }
}

// Geex reference frames 22/23 (workspace/design-refs/geex, tickets/
// brief-product-ui-direction.md "pastel tonal transaction rows") — the
// credit ledger is the one money/quantity row surface on this page that
// actually matches the reference's shape (an amount + a reason + a date
// per row), so only Section_Ledger below gets this treatment; Balance/Rates
// keep the plain AdminStatusRow they share today. Hue rotates by row
// POSITION via rowHueRotation('pastel'), same registered function
// BroadcastNewsRow's 'muted' family already uses. Credit vs. debit reads
// through the icon (arrowUp/arrowDown), not through recoloring the amount
// text — the reference's own rows are uniformly black ink regardless of
// sign, and this ledger's tone (red/green/caption) is exactly that signal.
//
// 2026-09-24, design brief 4.5: the pastel rows retired. A hue surface is a
// poster moment (brief C7) and the ledger is data, so it is now the shared
// table kit's row — neutral inks, `controlH`, the `hover` fill, no
// resting fill — with the amount in the kit's numeric cell. The sign still
// reads through the arrow; the icon sits on the neutral `widgetPlate`.
//
// 2026-09-24 (admin fix lane D): a record, not a control. Every row carried a
// click-to-copy handler on a role="row" <div> — 25 click targets no keyboard
// reached (admin census). The copy line was a diagnostics affordance of
// /admin/status; the amounts stay selectable text.
export const AdminLedgerRow = {
  extends: 'AppTableRow',
  columns: 'minmax(0, 1fr) auto',
  '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 'Z' },

  Left: {
    flow: 'x',
    align: 'center',
    gap: 'Z1',
    minWidth: '0',

    IconChip: {
      boxSize: 'C',
      round: '50%',
      flexShrink: '0',
      align: 'center center',
      background: 'widgetPlate',
      Icon: {
        name: (el, s) =>
          s.tone === 'red' ? 'arrowDown' : s.tone === 'green' ? 'arrowUp' : 'minus',
        boxSize: 'A',
        color: 'title'
      }
    },

    Text: {
      flow: 'y',
      gap: 'V',
      minWidth: '0',
      Hint: {
        hide: (el, s) => !s.hint,
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        // KEY-IN-STATE, like the sibling `Label` below: a producer may hand
        // this a bundle key, an already-interpolated sentence, or plain data.
        // polyglot resolves the first and returns the other two verbatim.
        text: (el, s) => el.call('polyglot', s.hint || '')
      },
      Label: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => el.call('polyglot', s.label || '')
      }
    }
  },

  Value: {
    extends: 'AppTableNum',
    tag: 'span',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => s.value
  }
}

// A label / value line of /admin/usage — the balance, the period totals, the
// tool turns, the by-reason list, the rate cards, the top-up prices, the
// system spend. The shared table kit's record row: `controlH`, the `hover`
// fill, no stroke, inside an AppTable.
//
// It is a RECORD, not a control (admin fix lane D, 2026-09-24). These lists
// extended /admin/status's diagnostics row (AdminStatusRow) and so carried its
// click-to-copy handler: 49 click targets no keyboard reached on the census
// build, then 49 buttons in the tab order once that row became a <button> —
// none of them an action a billing reader takes. The amounts stay selectable
// text.
//
// `value` is an amount by default — the kit's numeric cell (the `Code` face at
// `Z`, right-aligned, tabular). A row whose value is a WORD (a tier, a date, a
// pool, a period) sets `word: true` and keeps the house sans. The ink is
// neutral: `title`, the quiet `caption` for an empty or zero line, and
// `dangerInk` only for a failed read (`tone: 'red'`) — a green "has credits"
// is not a status, and the ledger beside it is neutral on purpose.
export const AdminUsageRow = {
  extends: 'AppTableRow',
  columns: 'minmax(0, 1fr) auto',
  '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 'V' },

  Left: {
    flow: 'y',
    gap: 'V',
    minWidth: '0',
    Label: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      overflowWrap: 'anywhere',
      // KEY-IN-STATE: a bundle key or a server-supplied name; polyglot
      // returns anything that is not a key verbatim.
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Hint: {
      hide: (el, s) => !s.hint,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => el.call('polyglot', s.hint || '')
    }
  },

  Value: {
    extends: 'AppTableNum',
    hide: (el, s) => !!s.word,
    tag: 'span',
    fontWeight: '500',
    color: (el, s) => (s.tone === 'red' ? 'dangerInk' : s.tone === 'caption' ? 'caption' : 'title'),
    '@mobileL': { justifySelf: 'start', textAlign: 'left' },
    text: (el, s) => s.value
  },

  Word: {
    hide: (el, s) => !s.word,
    tag: 'span',
    justifySelf: 'end',
    textAlign: 'right',
    fontSize: 'Z2',
    fontWeight: '500',
    color: (el, s) => (s.tone === 'red' ? 'dangerInk' : s.tone === 'caption' ? 'caption' : 'title'),
    '@mobileL': { justifySelf: 'start', textAlign: 'left' },
    text: (el, s) => s.value
  }
}
